import { Trans } from '@lingui/react/macro';

import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import { SparePartType, SparePartTypeFromGetMany } from '@/models/articleActionSparePart';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';
import { useDatabaseTranslation } from '@/services/i18n';
import { formatCurrency } from '@/utils/number';
import useViewPort from '@/utils/useViewport';

import { SparePartTypeImage } from './SparePartTypeImage';

export const SparePartDropdownItem = ({
  sparePartType,
}: {
  sparePartType: SparePartTypeFromGetMany;
}) => {
  const { isMobile } = useViewPort();
  const { _db } = useDatabaseTranslation();
  const { view: requestView } = useRequestContext();
  const { view } = useArticleContext();

  const showWorkshopPrice = requestView.workshopPrice.shown;
  const showOrganizationPrice = requestView.organizationPrice.shown;

  const showWorkshopAmountBeforeTaxes = requestView.workshopPrice.showAmountBeforeTaxes;
  const showOrganizationAmountBeforeTaxes = requestView.organizationPrice.showAmountBeforeTaxes;

  const unit = sparePartType.unit ? (
    _db(sparePartType.unit)
  ) : (
    <Trans id="spare-part-dropdown-item.part">part</Trans>
  );

  return (
    <Stack
      row
      alignItems="center"
      justifyContent="space-between"
      gap="0.5rem"
      flexWrap="nowrap"
      style={{ flex: 1 }}
    >
      <ActionSparePartReference sparePartType={sparePartType} />
      {view.services.actions.fields.spareParts.prices.shown && (
        <Stack
          {...(isMobile
            ? { alignItems: 'flex-end' }
            : { row: true, alignItems: 'center', gap: '0.5rem', flexWrap: 'nowrap' })}
        >
          {showWorkshopPrice && (
            <Paragraph weight="medium">
              {formatCurrency(
                showWorkshopAmountBeforeTaxes
                  ? sparePartType.workshopPrice?.amountBeforeTaxes
                  : sparePartType.workshopPrice?.amount,
                sparePartType.workshopPrice?.currency
              )}{' '}
              / {unit}
            </Paragraph>
          )}
          {showOrganizationPrice && (
            <Paragraph weight="medium">
              {formatCurrency(
                showOrganizationAmountBeforeTaxes
                  ? sparePartType.organizationPrice?.amountBeforeTaxes
                  : sparePartType.organizationPrice?.amount,
                sparePartType.organizationPrice?.currency
              )}{' '}
              / {unit}
            </Paragraph>
          )}
        </Stack>
      )}
    </Stack>
  );
};

const ActionSparePartReference = ({
  sparePartType,
  disabled = false,
}: {
  sparePartType: SparePartType;
  disabled?: boolean;
}) => {
  const { _db } = useDatabaseTranslation();
  const sparePartName = _db(sparePartType.name);

  return (
    <Stack row alignItems="center" gap="4px">
      <SparePartTypeImage sparePartType={sparePartType} />
      <InlineText className={disabled ? 'text-disabled' : ''}>{sparePartName}</InlineText>
    </Stack>
  );
};
